import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ArrowRight, MapPin } from "lucide-react";
import { festivalOn, fmtDay } from "../utils/festivals";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const emptyPilgrim = { name: "", gender: "", age: "", id_type: "", id_number: "" };
const idTypes = ["Aadhaar", "Passport", "Voter ID", "Driving Licence", "PAN Card"];
const genders = ["Male", "Female", "Other"];
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

export const BookingForm = ({ selected, onSubmit, submitting }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", pilgrims: "1", date: "" });
  const [pilgrims, setPilgrims] = useState([{ ...emptyPilgrim }]);
  const [slot, setSlot] = useState("");
  const [availability, setAvailability] = useState(null);
  const [error, setError] = useState("");
  const total = selected.price * Number(form.pilgrims || 1);
  const slots = availability?.slots || [];

  useEffect(() => {
    setAvailability(null);
    setSlot("");
    setForm((f) => ({ ...f, date: "" }));
    axios.get(`${API}/availability`, { params: { service: selected.name } })
      .then((r) => setAvailability(r.data))
      .catch(() => setAvailability({ slots: [], booked: {}, slot_capacity: 25, daily_capacity: 200 }));
  }, [selected]);

  const months = useMemo(() => {
    const out = [];
    const today = new Date();
    let current = null;
    for (let i = 1; i <= 90; i += 1) {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      if (!current || current.key !== key) {
        current = { key, label: d.toLocaleString("en-IN", { month: "long", year: "numeric" }), days: [], firstWeekday: d.getDay() };
        out.push(current);
      }
      current.days.push(d);
    }
    return out;
  }, []);

  const remainingOn = (dateStr) => {
    if (!availability) return null;
    const bookedOn = availability.booked?.[dateStr] || {};
    if (slots.length) return slots.reduce((sum, s) => sum + Math.max(0, availability.slot_capacity - (bookedOn[s] || 0)), 0);
    return Math.max(0, availability.daily_capacity - (bookedOn[""] || 0));
  };
  const slotRemaining = (s) => {
    if (!availability || !form.date) return 0;
    return Math.max(0, availability.slot_capacity - ((availability.booked?.[form.date] || {})[s] || 0));
  };

  const setCount = (count) => {
    setForm({ ...form, pilgrims: count });
    setPilgrims((prev) => Array.from({ length: Number(count) }, (_, i) => prev[i] || { ...emptyPilgrim }));
  };
  const setPilgrim = (i, key, value) =>
    setPilgrims((prev) => prev.map((p, idx) => (idx === i ? { ...p, [key]: value } : p)));

  const submit = (event) => {
    event.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.date)
      return setError("Please complete all contact details and pick a visit date.");
    if (slots.length && !slot)
      return setError("Please select a slot time for this seva.");
    if (pilgrims.some((p) => !p.name || !p.gender || !p.age || !p.id_type || !p.id_number))
      return setError("Please fill name, gender, age and photo ID for every pilgrim.");
    setError("");
    onSubmit({ ...form, slot, pilgrim_details: pilgrims, amount: total });
  };

  const dateFestival = form.date ? festivalOn(form.date) : null;

  return (
    <div className="booking-layout">
      <div className="selected-summary">
        <span className="summary-label">Selected seva</span>
        <h3>{selected.name}</h3>
        <p><MapPin size={14} /> {selected.place}</p>
        <div className="summary-price">₹{total.toLocaleString("en-IN")} <small>total reservation value</small></div>
        <small className="free-note">Pay securely by UPI in the next step</small>
      </div>
      <form onSubmit={submit} className="booking-form" data-testid="booking-form">
        <label>Full name<input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Primary contact name" data-testid="booking-name-input" /></label>
        <label>Email address<input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="E-ticket will be emailed here" data-testid="booking-email-input" /></label>
        <label>Mobile number<input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="10-digit mobile number" data-testid="booking-phone-input" /></label>
        <div className="form-row">
          <label>Pilgrims<select value={form.pilgrims} onChange={(e) => setCount(e.target.value)} data-testid="booking-pilgrims-select">{[1, 2, 3, 4, 5, 6].map((n) => <option key={n}>{n}</option>)}</select></label>
        </div>

        <p className="field-label">Visit date — live availability <small className="field-hint">(booking opens 1 day ahead · up to 90 days)</small></p>
        <div className="calendar" data-testid="booking-date-calendar">
          {months.map((m) => (
            <div className="cal-month" key={m.key} data-testid={`cal-month-${m.key}`}>
              <p className="cal-month-label">{m.label}</p>
              <div className="cal-weekdays">{WEEKDAYS.map((w) => <span key={w}>{w}</span>)}</div>
              <div className="cal-days">
                {Array.from({ length: m.firstWeekday }).map((_, i) => <span className="cal-blank" key={`b${i}`} />)}
                {m.days.map((d) => {
                  const dateStr = fmtDay(d);
                  const left = remainingOn(dateStr);
                  const full = left === 0;
                  const fest = festivalOn(dateStr);
                  return (
                    <button
                      type="button"
                      key={dateStr}
                      title={fest || dateStr}
                      className={`cal-day${form.date === dateStr ? " selected" : ""}${full ? " full" : left !== null && left <= 10 ? " low" : ""}${fest ? " fest" : ""}`}
                      disabled={full || left === null}
                      onClick={() => { setForm({ ...form, date: dateStr }); setSlot(""); }}
                      data-testid={`cal-day-${dateStr}`}
                    >
                      <b>{d.getDate()}</b>
                      {fest ? <i className="cal-fest">✦</i> : null}
                      <small>{left === null ? "…" : full ? "Full" : `${left} left`}</small>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {slots.length > 0 && form.date && (
          <>
            <p className="field-label">Slot time for {form.date}</p>
            <div className="slot-grid" data-testid="slot-grid">
              {slots.map((s) => {
                const left = slotRemaining(s);
                return (
                  <button
                    type="button"
                    key={s}
                    className={`slot-pill${slot === s ? " selected" : ""}`}
                    disabled={left === 0}
                    onClick={() => setSlot(s)}
                    data-testid={`slot-option-${s}`}
                  >
                    {s} hrs
                    <small>{left === 0 ? "Full" : `${left} seats left`}</small>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {dateFestival && <p className="festival-note" data-testid="festival-note">✦ {dateFestival} — a festival day at Tirumala. Expect larger crowds; please report early.</p>}

        {pilgrims.map((pilgrim, i) => (
          <fieldset className="pilgrim-card" key={i} data-testid={`pilgrim-card-${i}`}>
            <b>Pilgrim {String(i + 1).padStart(2, "0")}</b>
            <label className="pc-name">Pilgrim name<input value={pilgrim.name} onChange={(e) => setPilgrim(i, "name", e.target.value)} placeholder="As on photo ID" data-testid={`pilgrim-name-input-${i}`} /></label>
            <label className="pc-gender">Gender<select value={pilgrim.gender} onChange={(e) => setPilgrim(i, "gender", e.target.value)} data-testid={`pilgrim-gender-select-${i}`}><option value="">Select</option>{genders.map((g) => <option key={g}>{g}</option>)}</select></label>
            <label className="pc-age">Age<input type="number" min="1" max="110" value={pilgrim.age} onChange={(e) => setPilgrim(i, "age", e.target.value)} placeholder="Years" data-testid={`pilgrim-age-input-${i}`} /></label>
            <label className="pc-idtype">Photo ID type<select value={pilgrim.id_type} onChange={(e) => setPilgrim(i, "id_type", e.target.value)} data-testid={`pilgrim-id-type-select-${i}`}><option value="">Select</option>{idTypes.map((t) => <option key={t}>{t}</option>)}</select></label>
            <label className="pc-idnum">Photo ID number<input value={pilgrim.id_number} onChange={(e) => setPilgrim(i, "id_number", e.target.value)} placeholder="ID card number" data-testid={`pilgrim-id-number-input-${i}`} /></label>
          </fieldset>
        ))}
        {error && <p className="form-error" data-testid="booking-error-message">{error}</p>}
        <button className="primary-button form-submit" type="submit" disabled={submitting} data-testid="booking-submit-button">
          {submitting ? "Please wait…" : "Continue to payment"} <ArrowRight size={17} />
        </button>
      </form>
    </div>
  );
};
